import React, { useMemo, useState, useCallback, createContext, useContext, useEffect, useRef } from 'react';
import { isEqual } from 'lodash';
import { useLocation } from 'react-router-dom';
import { usePrevious, useUpdateEffect } from '@react-hookz/web';
import {
  useLocalStorageSynced,
  useSessionStorage,
  AbsoluteTimeRange,
  DurationString,
  isAbsoluteTimeRange,
  isRelativeTimeRange,
  TimeRangeValue,
  toAbsoluteTimeRange,
  getDurationStringSeconds,
} from '@chronosphereio/core';
import { useSetRefreshIntervalParams, useInternalTimeRange } from './query-params';
import { PinnedRange, PinnedTimeRangeValue, PinStatus, DEFAULT_TIME_RANGE } from '@/core';
import { useAnalytics } from '@/plugin-system/model/analytics';

export interface TimeRangeProviderV1Props {
  defaultTimeRange?: TimeRangeValue;
  initialRefreshInterval?: DurationString;
  enableUrlParams?: boolean;
  children?: React.ReactNode;
  enablePinning?: boolean;
  /**
   * Key used to force an update and recompute internal state of TimeRangeProvider.
   * Useful when initial relative time range needs to update to 'now' (ex: set recomputeKey to absoluteTimeRange.end).
   * Better for performance since it does not cause children to unmount compared to using a React key.
   * The recomputeKey is appended to the refreshKey used in our TanStack Query implementation and forces data queries to rerun.
   */
  recomputeKey?: string;
  /**
   * Callback function that is triggered whenever the `timeRange` changes.
   * Can be used to synchronize nested `TimeRangeProvider`s and their parent components.
   * @param timeRange The new time range value
   */
  onTimeRangeChange?: (timeRange: TimeRangeValue) => void;
}

export type RefreshInterval = DurationString | null;

export interface TimeRange {
  timeRange: TimeRangeValue;
  absoluteTimeRange: AbsoluteTimeRange; // resolved absolute time for plugins to use
  setTimeRange: (value: TimeRangeValue) => void;
  refresh: () => void;
  refreshKey: string;
  refreshInterval: RefreshInterval;
  refreshIntervalInMs: number | null;
  setRefreshInterval: (value: RefreshInterval) => void;
  /**
   * Whether the current time range is relative or absolute.
   */
  isRelative: boolean;
  /**
   * The range in milliseconds.
   */
  rangeMs: number;
  enableTimeRangePinning: boolean;
  pinnedTimeRange: PinnedTimeRangeValue;
  setPinnedTimeRange: (value: PinnedRange) => void;
  pinStatus: PinStatus;
  unpinTimeRange: () => void;
}

export const TimeRangeContext = createContext<TimeRange | undefined>(undefined);

/**
 * Get and set the current resolved time range at runtime.
 */
export function useTimeRange(): TimeRange {
  const ctx = useContext(TimeRangeContext);
  if (ctx === undefined) {
    throw new Error('No TimeRangeContext found. Did you forget a Provider?');
  }
  return ctx;
}

export const PINNED_TIME_RANGE_KEY = 'CHRONOSPHERE_PINNED_TIME_RANGE';
export const PINNED_TIME_RANGE_ACTIVE_KEY = 'CHRONOSPHERE_PINNED_TIME_RANGE_ACTIVE';

/**
 * Provider implementation that supplies the time range state at runtime.
 */
export function TimeRangeProviderV1({
  enableUrlParams: enabledURLParams = false,
  defaultTimeRange: initialTimeRange = DEFAULT_TIME_RANGE,
  initialRefreshInterval,
  children,
  enablePinning: enableTimeRangePinning = true,
  onTimeRangeChange,
  recomputeKey,
}: TimeRangeProviderV1Props) {
  const location = useLocation();
  const previousLocation = useRef(location);
  const pinStatusRef = useRef<PinStatus>();

  const { timeRange, setTimeRange: setTimeRangeInternal } = useInternalTimeRange({
    initialTimeRange,
    enabledURLParams,
    recomputeKey,
  });

  const prevTimeRange = usePrevious(timeRange);

  const [pinnedTimeRange, setPinnedTimeRangeInternal] = useLocalStorageSynced<PinnedTimeRangeValue>(
    PINNED_TIME_RANGE_KEY,
    {
      value: undefined,
      tabCount: 0,
      backup: undefined,
    }
  );
  const [pinnedTimeRangeActive, setPinnedTimeRangeActive] = useSessionStorage<boolean>(
    PINNED_TIME_RANGE_ACTIVE_KEY,
    false
  );

  const pinStatus: PinStatus = useMemo(() => {
    if (enableTimeRangePinning && pinnedTimeRange.tabCount > 0 && pinnedTimeRangeActive) {
      pinStatusRef.current = PinStatus.PINNED;
      return PinStatus.PINNED;
    } else if (enableTimeRangePinning && pinnedTimeRange.tabCount > 0 && !pinnedTimeRangeActive) {
      pinStatusRef.current = PinStatus.PINNED_IN_DIFFERENT_TAB;
      return PinStatus.PINNED_IN_DIFFERENT_TAB;
    } else {
      pinStatusRef.current = PinStatus.UNPINNED;
      return PinStatus.UNPINNED;
    }
  }, [enableTimeRangePinning, pinnedTimeRange.tabCount, pinnedTimeRangeActive]);

  const setTimeRange = useCallback(
    (value: TimeRangeValue) => {
      // if time range is pinned and a new range is selected, update the pin to the new range
      if (pinStatus === PinStatus.PINNED) {
        setPinnedTimeRangeInternal({
          value: value,
          tabCount: pinnedTimeRange.tabCount,
          backup: { value: value, tabCount: pinnedTimeRange.tabCount },
        });
      }
      // store the pin value as a backup so we can rehydrate it on page refresh
      setTimeRangeInternal(value, 'pushIn');
    },
    [pinStatus, pinnedTimeRange.tabCount, setPinnedTimeRangeInternal, setTimeRangeInternal]
  );

  // Allows parent to access time range, useful when nesting providers.
  // Do not call on mount, only on updates to previous timeRange.
  useUpdateEffect(() => {
    if (onTimeRangeChange && !isEqual(timeRange, prevTimeRange)) {
      onTimeRangeChange(timeRange);
    }
  }, [timeRange, onTimeRangeChange, prevTimeRange]);

  const setPinnedTimeRange = useCallback(
    (value: PinnedRange) => {
      // store the pin value as a backup so we can rehydrate it on page refresh
      setPinnedTimeRangeInternal({ ...value, backup: value });

      // if new tabCount is greater than current, we are pinning in a new tab so set pinnedTimeRangeActive to true
      if (value.tabCount > pinnedTimeRange.tabCount) {
        setPinnedTimeRangeActive(true);
      }
      // if new tabCount is less than current, we are unpinning in the current tab so set pinnedTimeRangeActive to false
      else if (value.tabCount < pinnedTimeRange.tabCount) {
        setPinnedTimeRangeActive(false);
      }
    },
    [pinnedTimeRange.tabCount, setPinnedTimeRangeActive, setPinnedTimeRangeInternal]
  );

  const unpinTimeRange = useCallback(() => {
    setPinnedTimeRange({
      ...pinnedTimeRange,
      tabCount: pinnedTimeRange.tabCount - 1,
    });
  }, [pinnedTimeRange, setPinnedTimeRange]);

  // If the pinned time range changes, update the time range to match
  useEffect(() => {
    if (pinStatus !== PinStatus.PINNED || !pinnedTimeRange.value) {
      return;
    }
    // When we save the date range to local storage it gets converted to a string so we need to
    // convert it back to Date objects before we can work with it
    const pinnedValue = isAbsoluteTimeRange(pinnedTimeRange.value)
      ? {
          start: new Date(pinnedTimeRange.value.start),
          end: new Date(pinnedTimeRange.value.end),
        }
      : pinnedTimeRange.value;

    if (!isEqual(pinnedValue, timeRange)) {
      setTimeRangeInternal(pinnedValue, 'replaceIn');
    }
    // Excluding timeRange from the dependency array to prevent the time range from being overridden when user clicks browser back button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableTimeRangePinning, pinStatus, pinnedTimeRange, setTimeRange, setTimeRangeInternal]);

  // Unpin time range if back button is clicked
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (location === undefined || !event.state) return;

      // restore any variable query params to make sure the back button only affects the time range
      if (location.pathname === previousLocation.current.pathname) {
        if (pinStatusRef.current === PinStatus.PINNED) {
          unpinTimeRange();
        }
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // we only need to set this on mount / unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey(refreshKey + 1);
  }, [refreshKey]);

  const { refreshInterval, setRefreshInterval, refreshIntervalInMs } = useAutoRefresh({
    refresh,
    initialRefreshInterval,
    enabledURLParams,
  });

  const ctx = useMemo(() => {
    const isRelative = isRelativeTimeRange(timeRange);
    const absoluteTimeRange = isRelative ? toAbsoluteTimeRange(timeRange) : timeRange;
    const rangeMs = absoluteTimeRange.end.valueOf() - absoluteTimeRange.start.valueOf();

    return {
      isRelative,
      timeRange,
      setTimeRange,
      absoluteTimeRange,
      refresh,
      refreshKey: `${absoluteTimeRange.start}:${absoluteTimeRange.end}:${refreshInterval}:${refreshKey}:${recomputeKey}`,
      refreshInterval,
      refreshIntervalInMs,
      setRefreshInterval,
      rangeMs,
      pinnedTimeRange,
      setPinnedTimeRange,
      enableTimeRangePinning,
      pinStatus,
      unpinTimeRange,
    };
  }, [
    timeRange,
    setTimeRange,
    refresh,
    refreshInterval,
    refreshKey,
    refreshIntervalInMs,
    setRefreshInterval,
    pinnedTimeRange,
    setPinnedTimeRange,
    enableTimeRangePinning,
    pinStatus,
    unpinTimeRange,
    recomputeKey,
  ]);

  // Analytics tracking
  const analytics = useAnalytics();
  useEffect(() => {
    analytics?.track('time_range_settings', {
      pinStatus: ctx.pinStatus,
    });
  }, [analytics, ctx.pinStatus]);

  return <TimeRangeContext.Provider value={ctx}>{children}</TimeRangeContext.Provider>;
}

type AutoRefreshProps = {
  initialRefreshInterval?: RefreshInterval;
  refresh: () => void;
  enabledURLParams?: boolean;
};

export function useAutoRefresh({ initialRefreshInterval, refresh, enabledURLParams = false }: AutoRefreshProps) {
  const { refreshInterval, setRefreshInterval } = useSetRefreshIntervalParams({
    initialRefreshInterval,
    enabledURLParams,
  });

  const refreshIntervalInSeconds = refreshInterval ? getDurationStringSeconds(refreshInterval) : null;
  const refreshIntervalInMs = refreshIntervalInSeconds ? refreshIntervalInSeconds * 1000 : null;

  useEffect(() => {
    if (!refreshIntervalInMs) return;
    const intervalIdx = setInterval(() => {
      refresh();
    }, refreshIntervalInMs);
    return () => {
      clearInterval(intervalIdx);
    };
  }, [refreshIntervalInMs, refresh]);

  return {
    refreshInterval,
    setRefreshInterval,
    refreshIntervalInMs,
  };
}
